const PERSON_NAME_PATTERN = /^[A-Za-z][A-Za-z '.-]*$/;
const TEXT_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 '&.,()/:-]*$/;
const PHONE_PATTERN = /^\+?[1-9]\d{9,14}$/;
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const AADHAAR_PATTERN = /^\d{12}$/;
const GST_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export function normalizeWhitespace(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

export function sanitizeEmail(value) {
  return normalizeWhitespace(value).toLowerCase();
}

export function sanitizePhone(value) {
  const normalized = normalizeWhitespace(value).replace(/[\s().-]/g, '');
  if (!normalized) return '';
  if (normalized.startsWith('00')) {
    return `+${normalized.slice(2)}`;
  }
  return normalized;
}

export function sanitizeDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

export function sanitizePan(value) {
  return normalizeWhitespace(value).replace(/\s+/g, '').toUpperCase();
}

export function sanitizeGst(value) {
  return normalizeWhitespace(value).replace(/\s+/g, '').toUpperCase();
}

function isEmpty(value) {
  return normalizeWhitespace(value) === '';
}

function validatePersonName(value, label, errors, key) {
  const normalized = normalizeWhitespace(value);
  if (!normalized) {
    errors[key] = `${label} is required.`;
  } else if (normalized.length < 2) {
    errors[key] = `${label} must be at least 2 characters.`;
  } else if (normalized.length > 150) {
    errors[key] = `${label} must be 150 characters or fewer.`;
  } else if (!PERSON_NAME_PATTERN.test(normalized)) {
    errors[key] = `${label} can contain letters, spaces, apostrophes, periods, and hyphens only.`;
  }
}

function validateText(value, label, errors, key, { required = true, max = 150 } = {}) {
  const normalized = normalizeWhitespace(value);
  if (!normalized) {
    if (required) errors[key] = `${label} is required.`;
    return;
  }
  if (normalized.length > max) {
    errors[key] = `${label} must be ${max} characters or fewer.`;
    return;
  }
  if (!TEXT_PATTERN.test(normalized)) {
    errors[key] = `${label} contains invalid characters.`;
  }
}

function validateEmail(value, errors, key, { required = true } = {}) {
  const normalized = sanitizeEmail(value);
  if (!normalized) {
    if (required) errors[key] = 'Email is required.';
    return;
  }
  if (normalized.length > 254 || !EMAIL_PATTERN.test(normalized)) {
    errors[key] = 'Enter a valid email address.';
  }
}

function validatePhone(value, errors, key) {
  const normalized = sanitizePhone(value);
  if (!normalized) {
    errors[key] = 'Mobile number is required.';
  } else if (!PHONE_PATTERN.test(normalized)) {
    errors[key] = 'Enter a valid mobile number with 10 to 15 digits.';
  }
}

function validateDateOfBirth(value, errors, key, { minAge = 18, maxAge = 75 } = {}) {
  const normalized = normalizeWhitespace(value);
  if (!normalized) {
    errors[key] = 'Date of birth is required.';
    return;
  }
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    errors[key] = 'Enter a valid date of birth.';
    return;
  }
  const now = new Date();
  if (date > now) {
    errors[key] = 'Date of birth cannot be in the future.';
    return;
  }
  let age = now.getFullYear() - date.getFullYear();
  const monthDiff = now.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) {
    age -= 1;
  }
  if (age < minAge) {
    errors[key] = `Applicant must be at least ${minAge} years old.`;
  } else if (age > maxAge) {
    errors[key] = `Applicant age must be ${maxAge} years or below.`;
  }
}

function validatePan(value, errors, key) {
  const normalized = sanitizePan(value);
  if (!normalized) {
    errors[key] = 'PAN number is required.';
  } else if (!PAN_PATTERN.test(normalized)) {
    errors[key] = 'Enter a valid PAN number.';
  }
}

function validateAadhaar(value, errors, key) {
  const normalized = sanitizeDigits(value);
  if (!normalized) {
    errors[key] = 'Aadhaar number is required.';
  } else if (!AADHAAR_PATTERN.test(normalized)) {
    errors[key] = 'Enter a valid 12-digit Aadhaar number.';
  }
}

function validateGst(value, errors, key) {
  const normalized = sanitizeGst(value);
  if (!normalized) return;
  if (!GST_PATTERN.test(normalized)) {
    errors[key] = 'Enter a valid GST number.';
  }
}

function validatePositiveNumber(value, label, errors, key, { min = 1, max = 1000000000, allowZero = false } = {}) {
  const normalized = normalizeWhitespace(value);
  if (!normalized) {
    errors[key] = `${label} is required.`;
    return;
  }
  const number = Number(normalized);
  if (!Number.isFinite(number)) {
    errors[key] = `${label} must be a valid number.`;
    return;
  }
  if (!allowZero && number <= 0) {
    errors[key] = `${label} must be greater than 0.`;
    return;
  }
  if (allowZero && number < 0) {
    errors[key] = `${label} cannot be negative.`;
    return;
  }
  if (number < min) {
    errors[key] = `${label} must be at least ${min}.`;
    return;
  }
  if (number > max) {
    errors[key] = `${label} is too large.`;
  }
}

function validateInteger(value, label, errors, key, { min = 0, max = 100 } = {}) {
  const normalized = normalizeWhitespace(value);
  if (!normalized) {
    errors[key] = `${label} is required.`;
    return;
  }
  const number = Number(normalized);
  if (!Number.isInteger(number)) {
    errors[key] = `${label} must be a whole number.`;
    return;
  }
  if (number < min || number > max) {
    errors[key] = `${label} must be between ${min} and ${max}.`;
  }
}

function validateSelect(value, label, errors, key, options) {
  const normalized = normalizeWhitespace(value);
  if (!normalized) {
    errors[key] = `${label} is required.`;
  } else if (!options.includes(normalized)) {
    errors[key] = `Select a valid ${label.toLowerCase()}.`;
  }
}

function validateOptionalText(value, label, errors, key, { max = 500, min = 0 } = {}) {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return;
  if (normalized.length < min) {
    errors[key] = `${label} must be at least ${min} characters.`;
    return;
  }
  if (normalized.length > max) {
    errors[key] = `${label} must be ${max} characters or fewer.`;
    return;
  }
  if (!TEXT_PATTERN.test(normalized)) {
    errors[key] = `${label} contains invalid characters.`;
  }
}

function validateFiles(files, docs, errors) {
  docs.forEach((doc) => {
    const file = files?.[doc.key];
    if (!(file instanceof File)) {
      errors[doc.key] = `${doc.label} is required.`;
      return;
    }
    if (file.size <= 0) {
      errors[doc.key] = `${doc.label} is empty.`;
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      errors[doc.key] = `${doc.label} must be 10 MB or smaller.`;
      return;
    }
    const suffix = `.${String(file.name || '').split('.').pop()?.toLowerCase() || ''}`;
    const allowed = doc.accept.split(',').map((item) => item.trim().toLowerCase());
    if (!allowed.includes(suffix)) {
      errors[doc.key] = `${doc.label} must match one of: ${doc.accept}.`;
    }
  });
}

export const LOAN_PRODUCT_DOCUMENTS = {
  home: [
    { key: 'panCard', label: 'PAN Card', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'aadhaarCard', label: 'Aadhaar Card', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'salarySlips', label: 'Salary Slips (Last 3 Months)', accept: '.pdf' },
    { key: 'bankStatement', label: 'Bank Statement (6 Months)', accept: '.pdf' },
    { key: 'propertyDocs', label: 'Property Documents', accept: '.pdf' },
    { key: 'photograph', label: 'Passport Size Photo', accept: '.jpg,.jpeg,.png' },
  ],
  business: [
    { key: 'panCard', label: 'PAN Card', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'aadhaarCard', label: 'Aadhaar Card', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'gstCert', label: 'GST Certificate', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'itr', label: 'ITR (Last 2 Years)', accept: '.pdf' },
    { key: 'bankStatement', label: 'Bank Statement (6 Months)', accept: '.pdf' },
    { key: 'bizProof', label: 'Business Registration Proof', accept: '.pdf,.jpg,.jpeg,.png' },
  ],
  personal: [
    { key: 'panCard', label: 'PAN Card', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'aadhaarCard', label: 'Aadhaar Card', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'salarySlips', label: 'Salary Slips (Last 3 Months)', accept: '.pdf' },
    { key: 'bankStatement', label: 'Bank Statement (6 Months)', accept: '.pdf' },
    { key: 'photograph', label: 'Photograph', accept: '.jpg,.jpeg,.png' },
  ],
  car: [
    { key: 'panCard', label: 'PAN Card', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'aadhaarCard', label: 'Aadhaar Card', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'salarySlips', label: 'Salary Slips', accept: '.pdf' },
    { key: 'bankStatement', label: 'Bank Statement', accept: '.pdf' },
    { key: 'dealerQuote', label: 'Dealer Quotation', accept: '.pdf,.jpg,.jpeg,.png' },
  ],
  education: [
    { key: 'studentId', label: 'Student ID / Marksheet', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'admission', label: 'Admission Letter', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'feeStructure', label: 'Fee Structure', accept: '.pdf' },
    { key: 'panCard', label: 'PAN Card (Co-Applicant)', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'aadhaarCard', label: 'Aadhaar (Co-Applicant)', accept: '.pdf,.jpg,.jpeg,.png' },
    { key: 'incomeProof', label: 'Income Proof of Co-Applicant', accept: '.pdf' },
    { key: 'bankStatement', label: 'Bank Statement (6 Months)', accept: '.pdf' },
  ],
};

function validateHomeStep(stepIndex, state) {
  const errors = {};
  if (stepIndex === 0) {
    validatePersonName(state.basic.fullName, 'Full name', errors, 'fullName');
    validatePhone(state.basic.mobile, errors, 'mobile');
    validateEmail(state.basic.email, errors, 'email');
    validateDateOfBirth(state.basic.dob, errors, 'dob');
    validatePan(state.basic.pan, errors, 'pan');
    validateAadhaar(state.basic.aadhaar, errors, 'aadhaar');
    validateSelect(state.basic.employmentType, 'employment type', errors, 'employmentType', ['salaried', 'self_employed']);
  } else if (stepIndex === 1) {
    validatePositiveNumber(state.income.monthlyIncome, 'Monthly income', errors, 'monthlyIncome', { max: 100000000 });
    validateText(state.income.companyName, 'Company / Business name', errors, 'companyName');
    validateInteger(state.income.experience, 'Work experience', errors, 'experience', { min: 0, max: 60 });
  } else if (stepIndex === 2) {
    validateSelect(state.property.propertyType, 'property type', errors, 'propertyType', ['flat', 'house', 'plot']);
    validatePositiveNumber(state.property.propertyValue, 'Property value', errors, 'propertyValue');
    validatePositiveNumber(state.property.loanAmount, 'Loan amount', errors, 'loanAmount');
    validateText(state.property.propertyLocation, 'Property location', errors, 'propertyLocation');
    const propertyValue = Number(state.property.propertyValue || 0);
    const loanAmount = Number(state.property.loanAmount || 0);
    if (propertyValue && loanAmount > propertyValue) {
      errors.loanAmount = 'Loan amount cannot exceed property value.';
    }
  } else if (stepIndex === 3) {
    validateFiles(state.files, LOAN_PRODUCT_DOCUMENTS.home, errors);
  }
  return errors;
}

function validateBusinessStep(stepIndex, state) {
  const errors = {};
  if (stepIndex === 0) {
    validatePersonName(state.basic.fullName, 'Full name', errors, 'fullName');
    validatePhone(state.basic.mobile, errors, 'mobile');
    validateEmail(state.basic.email, errors, 'email');
    validateDateOfBirth(state.basic.dob, errors, 'dob');
    validatePan(state.basic.pan, errors, 'pan');
    validateAadhaar(state.basic.aadhaar, errors, 'aadhaar');
    validateSelect(state.basic.employmentType, 'employment type', errors, 'employmentType', ['salaried', 'self_employed']);
  } else if (stepIndex === 1) {
    validateText(state.business.businessName, 'Business name', errors, 'businessName');
    validateSelect(state.business.businessType, 'business type', errors, 'businessType', ['prop', 'part', 'pvt', 'llp']);
    validateInteger(state.business.vintage, 'Business vintage', errors, 'vintage', { min: 0, max: 80 });
    validatePositiveNumber(state.business.turnover, 'Annual turnover', errors, 'turnover');
    validateGst(state.business.gst, errors, 'gst');
    validatePositiveNumber(state.business.loanAmount, 'Loan amount', errors, 'loanAmount');
  } else if (stepIndex === 2) {
    validateFiles(state.files, LOAN_PRODUCT_DOCUMENTS.business, errors);
  }
  return errors;
}

function validatePersonalStep(stepIndex, state) {
  const errors = {};
  if (stepIndex === 0) {
    validatePersonName(state.data.fullName, 'Full name', errors, 'fullName');
    validatePhone(state.data.mobile, errors, 'mobile');
    validateEmail(state.data.email, errors, 'email');
    validatePan(state.data.pan, errors, 'pan');
    validateAadhaar(state.data.aadhaar, errors, 'aadhaar');
    validateSelect(state.data.employmentType, 'employment type', errors, 'employmentType', ['salaried', 'self_employed']);
    validateText(state.data.companyName, 'Company name', errors, 'companyName');
    validatePositiveNumber(state.data.monthlyIncome, 'Monthly income', errors, 'monthlyIncome', { max: 100000000 });
    validatePositiveNumber(state.data.loanAmount, 'Loan amount', errors, 'loanAmount');
    validateOptionalText(state.data.loanPurpose, 'Loan purpose', errors, 'loanPurpose', { min: 10, max: 300 });
  } else if (stepIndex === 1) {
    validateFiles(state.files, LOAN_PRODUCT_DOCUMENTS.personal, errors);
  }
  return errors;
}

function validateCarStep(stepIndex, state) {
  const errors = {};
  if (stepIndex === 0) {
    validatePersonName(state.personal.fullName, 'Full name', errors, 'fullName');
    validatePhone(state.personal.mobile, errors, 'mobile');
    validateEmail(state.personal.email, errors, 'email');
    validatePan(state.personal.pan, errors, 'pan');
    validateAadhaar(state.personal.aadhaar, errors, 'aadhaar');
    validateSelect(state.personal.employmentType, 'employment type', errors, 'employmentType', ['salaried', 'self_employed']);
    validateText(state.personal.companyName, 'Company name', errors, 'companyName');
    validatePositiveNumber(state.personal.monthlyIncome, 'Monthly income', errors, 'monthlyIncome', { max: 100000000 });
    validatePositiveNumber(state.personal.loanAmount, 'Loan amount', errors, 'loanAmount');
  } else if (stepIndex === 1) {
    validateSelect(state.vehicle.carType, 'car type', errors, 'carType', ['new', 'used']);
    validateText(state.vehicle.carModel, 'Car model', errors, 'carModel');
    validatePositiveNumber(state.vehicle.onRoadPrice, 'On-road price', errors, 'onRoadPrice');
    if (!isEmpty(state.vehicle.downPayment)) {
      validatePositiveNumber(state.vehicle.downPayment, 'Down payment', errors, 'downPayment', { min: 0, allowZero: true });
    }
    validateText(state.vehicle.dealerName, 'Dealer name', errors, 'dealerName', { required: false });
    const onRoadPrice = Number(state.vehicle.onRoadPrice || 0);
    const downPayment = Number(state.vehicle.downPayment || 0);
    if (onRoadPrice && downPayment >= onRoadPrice) {
      errors.downPayment = 'Down payment must be less than the on-road price.';
    }
    const loanAmount = Number(state.personal.loanAmount || 0);
    if (onRoadPrice && loanAmount > onRoadPrice) {
      errors.loanAmount = 'Loan amount cannot exceed the on-road price.';
    }
  } else if (stepIndex === 2) {
    validateFiles(state.files, LOAN_PRODUCT_DOCUMENTS.car, errors);
  }
  return errors;
}

function validateEducationStep(stepIndex, state) {
  const errors = {};
  if (stepIndex === 0) {
    validatePersonName(state.student.studentName, 'Student name', errors, 'studentName');
    validateDateOfBirth(state.student.dob, errors, 'dob', { minAge: 16, maxAge: 45 });
    validatePhone(state.student.mobile, errors, 'mobile');
    validateEmail(state.student.email, errors, 'email');
    validateAadhaar(state.student.aadhaar, errors, 'aadhaar');
    validateText(state.student.courseName, 'Course name', errors, 'courseName');
    validateText(state.student.collegeName, 'College / University', errors, 'collegeName');
    validateSelect(state.student.country, 'country of study', errors, 'country', ['india', 'usa', 'uk', 'canada', 'australia', 'other']);
    validateText(state.student.courseDuration, 'Course duration', errors, 'courseDuration', { max: 40 });
    validatePositiveNumber(state.student.loanAmount, 'Loan amount', errors, 'loanAmount');
  } else if (stepIndex === 1) {
    validatePersonName(state.coApplicant.coName, 'Co-applicant name', errors, 'coName');
    validateSelect(state.coApplicant.relationship, 'relationship', errors, 'relationship', ['father', 'mother', 'spouse', 'sibling', 'guardian']);
    validatePositiveNumber(state.coApplicant.coIncome, 'Co-applicant monthly income', errors, 'coIncome', { max: 100000000 });
  } else if (stepIndex === 2) {
    validateFiles(state.files, LOAN_PRODUCT_DOCUMENTS.education, errors);
  }
  return errors;
}

export function validateLoanApplicationStep(productType, stepIndex, state) {
  if (productType === 'home') return validateHomeStep(stepIndex, state);
  if (productType === 'business') return validateBusinessStep(stepIndex, state);
  if (productType === 'personal') return validatePersonalStep(stepIndex, state);
  if (productType === 'car') return validateCarStep(stepIndex, state);
  if (productType === 'education') return validateEducationStep(stepIndex, state);
  return {};
}

export function validateLoanApplicationSubmission(productType, totalSteps, state) {
  for (let stepIndex = 0; stepIndex < totalSteps - 1; stepIndex += 1) {
    const errors = validateLoanApplicationStep(productType, stepIndex, state);
    if (Object.keys(errors).length > 0) {
      return { success: false, stepIndex, errors };
    }
  }
  return { success: true, stepIndex: totalSteps - 1, errors: {} };
}

function buildDocumentPayload(files) {
  return Object.entries(files || {}).map(([key, file]) => ({
    key,
    file_name: file && file.name ? file.name.trim() : '',
  }));
}

export function buildLoanApplicationPayload(productType, state) {
  if (productType === 'home') {
    return {
      applicant_name: normalizeWhitespace(state.basic.fullName),
      phone: sanitizePhone(state.basic.mobile),
      email: sanitizeEmail(state.basic.email),
      loan_type: 'home',
      loan_amount: Number(state.property.loanAmount),
      product_details: {
        basic: {
          ...state.basic,
          fullName: normalizeWhitespace(state.basic.fullName),
          mobile: sanitizePhone(state.basic.mobile),
          email: sanitizeEmail(state.basic.email),
          pan: sanitizePan(state.basic.pan),
          aadhaar: sanitizeDigits(state.basic.aadhaar),
          employmentType: normalizeWhitespace(state.basic.employmentType),
        },
        income: {
          ...state.income,
          monthlyIncome: String(Number(state.income.monthlyIncome)),
          companyName: normalizeWhitespace(state.income.companyName),
          experience: String(Number(state.income.experience)),
        },
        property: {
          ...state.property,
          propertyType: normalizeWhitespace(state.property.propertyType),
          propertyValue: String(Number(state.property.propertyValue)),
          loanAmount: String(Number(state.property.loanAmount)),
          propertyLocation: normalizeWhitespace(state.property.propertyLocation),
        },
        documents: buildDocumentPayload(state.files),
      },
    };
  }

  if (productType === 'business') {
    return {
      applicant_name: normalizeWhitespace(state.basic.fullName),
      phone: sanitizePhone(state.basic.mobile),
      email: sanitizeEmail(state.basic.email),
      loan_type: 'business',
      loan_amount: Number(state.business.loanAmount),
      product_details: {
        basic: {
          ...state.basic,
          fullName: normalizeWhitespace(state.basic.fullName),
          mobile: sanitizePhone(state.basic.mobile),
          email: sanitizeEmail(state.basic.email),
          pan: sanitizePan(state.basic.pan),
          aadhaar: sanitizeDigits(state.basic.aadhaar),
          employmentType: normalizeWhitespace(state.basic.employmentType),
        },
        business: {
          ...state.business,
          businessName: normalizeWhitespace(state.business.businessName),
          businessType: normalizeWhitespace(state.business.businessType),
          vintage: String(Number(state.business.vintage)),
          turnover: String(Number(state.business.turnover)),
          gst: sanitizeGst(state.business.gst),
          loanAmount: String(Number(state.business.loanAmount)),
        },
        documents: buildDocumentPayload(state.files),
      },
    };
  }

  if (productType === 'personal') {
    return {
      applicant_name: normalizeWhitespace(state.data.fullName),
      phone: sanitizePhone(state.data.mobile),
      email: sanitizeEmail(state.data.email),
      loan_type: 'personal',
      loan_amount: Number(state.data.loanAmount),
      product_details: {
        ...state.data,
        fullName: normalizeWhitespace(state.data.fullName),
        mobile: sanitizePhone(state.data.mobile),
        email: sanitizeEmail(state.data.email),
        pan: sanitizePan(state.data.pan),
        aadhaar: sanitizeDigits(state.data.aadhaar),
        employmentType: normalizeWhitespace(state.data.employmentType),
        companyName: normalizeWhitespace(state.data.companyName),
        monthlyIncome: String(Number(state.data.monthlyIncome)),
        loanAmount: String(Number(state.data.loanAmount)),
        loanPurpose: normalizeWhitespace(state.data.loanPurpose),
        documents: buildDocumentPayload(state.files),
      },
    };
  }

  if (productType === 'car') {
    return {
      applicant_name: normalizeWhitespace(state.personal.fullName),
      phone: sanitizePhone(state.personal.mobile),
      email: sanitizeEmail(state.personal.email),
      loan_type: 'car',
      loan_amount: Number(state.personal.loanAmount),
      product_details: {
        personal: {
          ...state.personal,
          fullName: normalizeWhitespace(state.personal.fullName),
          mobile: sanitizePhone(state.personal.mobile),
          email: sanitizeEmail(state.personal.email),
          pan: sanitizePan(state.personal.pan),
          aadhaar: sanitizeDigits(state.personal.aadhaar),
          employmentType: normalizeWhitespace(state.personal.employmentType),
          companyName: normalizeWhitespace(state.personal.companyName),
          monthlyIncome: String(Number(state.personal.monthlyIncome)),
          loanAmount: String(Number(state.personal.loanAmount)),
        },
        vehicle: {
          ...state.vehicle,
          carType: normalizeWhitespace(state.vehicle.carType),
          carModel: normalizeWhitespace(state.vehicle.carModel),
          onRoadPrice: String(Number(state.vehicle.onRoadPrice)),
          downPayment: isEmpty(state.vehicle.downPayment) ? '' : String(Number(state.vehicle.downPayment)),
          dealerName: normalizeWhitespace(state.vehicle.dealerName),
        },
        documents: buildDocumentPayload(state.files),
      },
    };
  }

  return {
    applicant_name: normalizeWhitespace(state.student.studentName),
    phone: sanitizePhone(state.student.mobile),
    email: sanitizeEmail(state.student.email),
    loan_type: 'education',
    loan_amount: Number(state.student.loanAmount),
    product_details: {
      student: {
        ...state.student,
        studentName: normalizeWhitespace(state.student.studentName),
        mobile: sanitizePhone(state.student.mobile),
        email: sanitizeEmail(state.student.email),
        aadhaar: sanitizeDigits(state.student.aadhaar),
        courseName: normalizeWhitespace(state.student.courseName),
        collegeName: normalizeWhitespace(state.student.collegeName),
        country: normalizeWhitespace(state.student.country),
        courseDuration: normalizeWhitespace(state.student.courseDuration),
        loanAmount: String(Number(state.student.loanAmount)),
      },
      coApplicant: {
        ...state.coApplicant,
        coName: normalizeWhitespace(state.coApplicant.coName),
        relationship: normalizeWhitespace(state.coApplicant.relationship),
        coIncome: String(Number(state.coApplicant.coIncome)),
      },
      documents: buildDocumentPayload(state.files),
    },
  };
}
